import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Building2 } from "lucide-react";
import { HERO_COLLAGE } from "../data/content";
import { AnimatedCounter } from "../components/AnimatedCounter";
import { useSiteContent } from "../context/SiteContentContext";
import { HomeVideoTeaser } from "../components/VideoShowcase";

export default function HomePage() {
  const { org, stats, hero } = useSiteContent();
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section data-testid="hero-section" className="relative pt-32 md:pt-40 pb-20 md:pb-28 overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full cf-blob-pink" />
          <div className="absolute -bottom-32 -left-32 w-[520px] h-[520px] rounded-full cf-blob-black" />
        </div>

        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 items-center relative">
          <div className="lg:col-span-7 cf-rise">
            {/* <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass text-xs font-mono uppercase tracking-[0.2em] text-black" data-testid="hero-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EC4899] animate-pulse" />
              Bradford · Community Interest Company
            </span> */}
            <h1 className="font-display text-black text-5xl sm:text-6xl lg:text-[92px] leading-[0.95] tracking-tight mt-7">
              Welcome to <br />
              <span className="italic text-[#EC4899]">Creative Flare</span><br />
              Yorkshire.
            </h1>
            <p className="mt-8 text-lg md:text-xl max-w-xl text-black/70 leading-relaxed">
              {hero.body}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/what-we-do" data-testid="hero-cta-primary" className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#EC4899] text-white font-medium hover:bg-[#DB2777] transition-all hover:-translate-y-[2px] shadow-pink">
                What we do <ArrowUpRight size={18} />
              </Link>
              <Link to="/services" data-testid="hero-cta-secondary" className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-black/20 text-black font-medium hover:bg-black hover:text-white transition-all">
                Services & Programmes
              </Link>
            </div>
          </div>

          {/* Photo collage */}
          <div className="lg:col-span-5 relative h-[440px] md:h-[540px]" data-testid="hero-collage">
            <div className="absolute top-0 right-4 w-48 md:w-56 aspect-[3/4] rounded-3xl overflow-hidden shadow-hover cf-float-a">
              <img src={HERO_COLLAGE[0]} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-24 left-0 w-52 md:w-64 aspect-square rounded-3xl overflow-hidden shadow-pink cf-float-b">
              <img src={HERO_COLLAGE[1]} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute bottom-8 right-0 w-52 md:w-64 aspect-[4/3] rounded-3xl overflow-hidden shadow-hover cf-float-c">
              <img src={HERO_COLLAGE[2]} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute bottom-0 left-6 w-36 md:w-40 aspect-[3/4] rounded-3xl overflow-hidden shadow-hover cf-float-a" style={{ animationDelay: "1.5s" }}>
              <img src={HERO_COLLAGE[3]} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="absolute top-3 left-24 w-12 h-12 rounded-full glass grid place-items-center cf-float-b">
              <div className="w-2 h-2 rounded-full bg-[#EC4899]" />
            </div>
          </div>
        </div>
      </section>

      {/* WHO WE ARE */}
      <section data-testid="who-we-are" className="py-20 md:py-28 bg-[#FDF2F8]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Who we are</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-black mt-4 leading-[0.95]">Creative Flare C.I.C</h2>
          </div>
          <div className="lg:col-span-7 space-y-6 text-lg text-black/75 leading-relaxed">
            <p>Creative Flare Yorkshire. “Established in 2012, we are a community-based organisation dedicated to making a positive difference in communities across Yorkshire.“ </p> 
            {/* <p>As a Community Interest Company (No. {org.companyNumber}), every activity we run is designed with, and for, the community we serve. No exclusive spaces. No hidden barriers. Just warm, purposeful work.</p> */}
            <div className="grid sm:grid-cols-2 gap-4 pt-4">
              <div className="rounded-2xl p-5 bg-white border border-black/5 shadow-ambient">
                <Building2 size={22} className="text-[#EC4899]" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/50 mt-3">Registered office</p>
                <p className="text-sm text-black mt-1 leading-relaxed">{org.registeredAddress.line1}<br />{org.registeredAddress.line2}</p>
              </div>
              <div className="rounded-2xl p-5 bg-white border border-black/5 shadow-ambient">
                <MapPin size={22} className="text-[#EC4899]" />
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-black/50 mt-3">{org.venue.label}</p>
                <p className="text-sm text-black mt-1 leading-relaxed">{org.venue.name}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      {/* <section data-testid="home-stats" className="py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
            {stats.map((s, i) => (
              <div key={i} data-testid={`stat-card-${i}`} className="rounded-3xl p-8 border border-black/8 hover:border-[#EC4899]/60 hover:-translate-y-1 transition-all">
                <div className="w-10 h-1 rounded-full bg-[#EC4899] mb-6" />
                <div className="font-mono text-4xl md:text-5xl text-black tracking-tight">
                  <AnimatedCounter value={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-3 text-sm text-black/65">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      <HomeVideoTeaser />

      {/* SPLIT CTA */}
      <section className="px-6 md:px-10 lg:px-14 pb-24">
        <div className="max-w-[1440px] mx-auto rounded-[40px] p-10 md:p-16 lg:p-20 relative overflow-hidden bg-[#0A0A0A] text-white">
          <div aria-hidden className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full cf-blob-pink" />
          <div className="relative grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <p className="font-mono uppercase text-xs tracking-[0.25em] text-[#EC4899]">Get involved</p>
              <h2 className="font-display text-4xl md:text-6xl lg:text-7xl mt-5 leading-[0.95]">Join us. Bring a friend. <br /><span className="italic text-[#EC4899]">Let's make things happen.</span></h2>
            </div>
            <div className="lg:col-span-4 flex flex-col gap-3">
              <Link to="/services" data-testid="home-cta-join" className="inline-flex items-center justify-between px-7 py-5 rounded-full bg-[#EC4899] text-white font-medium hover:bg-[#DB2777] transition-all shadow-pink">
                Explore our services <ArrowUpRight size={18} />
              </Link>
              <Link to="/contact" data-testid="home-cta-contact" className="inline-flex items-center justify-between px-7 py-5 rounded-full border border-white/25 text-white hover:bg-white hover:text-black transition-all">
                Get in touch <ArrowUpRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
